import type * as React from 'react'

import {
  Flex,
  Heading,
  Sidebar,
  Text,
  useSidebar,
  useIsMobileScreenSize,
  type FlexProps,
} from '@buffer-mono/popcorn'

import { usePageTitle } from '~publish/hooks/usePageTitle'

import style from './SettingsPageLayout.module.css'

type NewSettingsPageLayoutProps = {
  children: React.ReactNode
  title: string
  subtitle?: string | React.ReactNode
  wide?: boolean
  actions?: React.ReactNode
  gap?: FlexProps['gap']
  testId?: string
}

export const NewSettingsPageLayout = ({
  children,
  title,
  subtitle,
  wide = false,
  actions = null,
  gap = 'xs',
  testId = `${title.toLowerCase()}-settings-page`,
}: NewSettingsPageLayoutProps): JSX.Element => {
  usePageTitle(title)

  const { setOpen, open } = useSidebar()
  const isMobile = useIsMobileScreenSize()

  const toggleSidebar = (): void => {
    setOpen(!open)
  }

  return (
    <div className={style.wrapper} data-testid={testId}>
      <div className={wide ? style.innerWide : style.inner}>
        <Flex direction="column" gap="lg" className={style.innerContent}>
          <Flex
            as="header"
            gap="sm"
            direction={isMobile ? 'column' : 'row'}
            align={'start'}
            justify={isMobile ? 'start' : 'between'}
            fullWidth
            className={style.header}
          >
            <Heading as="h1" size={isMobile ? 'small' : 'large'}>
              <Flex align="center">
                {isMobile && <Sidebar.Trigger onClick={toggleSidebar} />}
                <Flex direction="column" gap="2xs">
                  {title}
                  {subtitle && !isMobile && (
                    <Text as="p" color="subtle">
                      {subtitle}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Heading>
            {actions}
          </Flex>
          <Flex
            as="main"
            direction="column"
            gap={gap}
            fullWidth
            // todo move to class
            style={{ paddingBottom: 'var(--space-2xl)' }}
          >
            {children}
          </Flex>
        </Flex>
      </div>
    </div>
  )
}
